import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EVENT_FORMATS, formatDate, isoDay, type EventDto, type EventFormat } from '@pb/shared';
import { createEvent, listEvents } from '../api/events';
import { listPresentations } from '../api/presentations';
import { Dialog } from '../components/Dialog';
import { EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { useProject } from '../lib/project';
import { pluralize } from '../lib/text';
import { useResource } from '../lib/useResource';
import { CreatePresentationDialog } from './PresentationsPage';

export function formatLabel(format: EventFormat): string {
  return format.charAt(0).toUpperCase() + format.slice(1);
}

function CreateEventDialog({ onClose }: { onClose: () => void }) {
  const { project } = useProject();
  const navigate = useNavigate();
  const presentations = useResource(() => listPresentations(project.id), [project.id]);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(isoDay(new Date()));
  const [time, setTime] = useState('');
  const [format, setFormat] = useState<EventFormat>('in person');
  const [location, setLocation] = useState('');
  const [audience, setAudience] = useState('');
  const [chosen, setChosen] = useState<number[]>([]);
  const [creatingPresentation, setCreatingPresentation] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async () => {
    if (!title.trim() || !date) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createEvent(project.id, {
        title: title.trim(),
        date,
        start_time: time || null,
        format,
        location,
        audience,
        attendees: [],
        notes: '',
        presentation_ids: chosen,
      });
      navigate(`/projects/${project.id}/events/${created.id}`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const available = (presentations.data ?? []).filter((p) => !p.retired);

  return (
    <>
      <Dialog
        title="New event"
        wide
        onClose={onClose}
        closeDisabled={busy}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              form="event-create-form"
              className="btn btn-primary"
              disabled={busy || !title.trim() || !date}
            >
              {busy ? 'Creating…' : 'Create event'}
            </button>
          </>
        }
      >
        <form
          id="event-create-form"
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="field span-2">
            <label htmlFor="ev-title">Title</label>
            <input id="ev-title" className="input" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="ev-date">Date</label>
            <input id="ev-date" type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="ev-time">Start time (optional)</label>
            <input id="ev-time" type="time" className="input" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ev-format">Format</label>
            <select id="ev-format" className="select" value={format} onChange={(e) => setFormat(e.target.value as EventFormat)}>
              {EVENT_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {formatLabel(f)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ev-location">Location (optional)</label>
            <input id="ev-location" className="input" value={location} maxLength={500} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div className="field span-2">
            <label htmlFor="ev-audience">Audience (optional)</label>
            <input id="ev-audience" className="input" value={audience} maxLength={1000} onChange={(e) => setAudience(e.target.value)} />
          </div>
          <fieldset className="field span-2" style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className="field-label" style={{ marginBottom: 4 }}>
              Presentations
            </legend>
            {presentations.error ? (
              <ErrorAlert error={presentations.error} />
            ) : presentations.data === undefined ? (
              <Loading />
            ) : available.length === 0 ? (
              <div className="row-wrap">
                <p className="small muted">This project has no presentations yet. You can add them to the event later.</p>
                <button type="button" className="btn btn-sm" onClick={() => setCreatingPresentation(true)} disabled={busy}>
                  Create a presentation
                </button>
              </div>
            ) : (
              <div className="picker-list">
                {available.map((p) => {
                  const on = chosen.includes(p.id);
                  return (
                    <label key={p.id} className="picker-item checkbox">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => setChosen((c) => (on ? c.filter((x) => x !== p.id) : [...c, p.id]))}
                        aria-label={p.name}
                      />
                      <span className="grow">{p.name}</span>
                      {on ? <span className="badge badge-accent">#{chosen.indexOf(p.id) + 1}</span> : null}
                    </label>
                  );
                })}
              </div>
            )}
            <span className="field-hint">The order in which you tick them is the order at the event; you can change it later.</span>
          </fieldset>
        </form>
        <ErrorAlert error={error} />
      </Dialog>
      {creatingPresentation ? (
        <CreatePresentationDialog
          onClose={() => setCreatingPresentation(false)}
          onCreated={(created) => {
            presentations.setData([...(presentations.data ?? []), created]);
            setChosen((c) => (c.includes(created.id) ? c : [...c, created.id]));
            setCreatingPresentation(false);
          }}
        />
      ) : null}
    </>
  );
}

function EventTable({ events, projectId }: { events: EventDto[]; projectId: number }) {
  return (
    <div className="card">
      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Title</th>
            <th>Format</th>
            <th>Audience</th>
            <th>Presentations</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id}>
              <td className="nowrap">
                {formatDate(e.date)}
                {e.start_time ? <span className="faint"> {e.start_time}</span> : null}
              </td>
              <td>
                <Link to={`/projects/${projectId}/events/${e.id}`}>
                  <strong>{e.title}</strong>
                </Link>
                {e.location ? <div className="small muted">{e.location}</div> : null}
              </td>
              <td>{formatLabel(e.format)}</td>
              <td className="small">{e.audience || <span className="faint">—</span>}</td>
              <td>{pluralize(e.presentations.length, 'presentation')}</td>
              <td>
                {e.status === 'held' ? (
                  <span className="badge badge-success">Held</span>
                ) : e.not_marked_held || e.date < isoDay(new Date()) ? (
                  <span className="badge badge-warning">Not marked held</span>
                ) : (
                  <span className="badge badge-accent">Planned</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EventsPage() {
  const { project } = useProject();
  const events = useResource(() => listEvents(project.id), [project.id]);
  const [creating, setCreating] = useState(false);
  const today = isoDay(new Date());
  const all = events.data ?? [];
  const upcoming = all.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = all.filter((e) => e.date < today).sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="section">
      <div className="page-header">
        <div className="page-title">
          <h2>Events</h2>
          <p className="muted small">Occasions at which presentations are given, with an exact copy of what was shown.</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            New event
          </button>
        </div>
      </div>
      {events.error ? (
        <ErrorState error={events.error} onRetry={() => void events.reload()} />
      ) : events.data === undefined ? (
        <Loading label="Loading events…" />
      ) : all.length === 0 ? (
        <EmptyState
          title="No events yet"
          action={
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Create an event
            </button>
          }
        >
          Create an event for the next meeting, attach the presentations you will give, and generate them.
        </EmptyState>
      ) : (
        <>
          <section className="stack">
            <h3>Upcoming</h3>
            {upcoming.length ? (
              <EventTable events={upcoming} projectId={project.id} />
            ) : (
              <p className="muted small">No upcoming events.</p>
            )}
          </section>
          <section className="stack">
            <h3>Past</h3>
            {past.length ? <EventTable events={past} projectId={project.id} /> : <p className="muted small">No past events.</p>}
          </section>
        </>
      )}
      {creating ? <CreateEventDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}
